/**
 * UserDetail.tsx — F12.M Etap D Part 2 (Users fullscreen, Etap 1.4 + 1.4b).
 *
 * Strona szczegółów Użytkownika. Analog do `EmployeeDetail.tsx`. Refactor z
 * `UserDetailDrawer` w `Users.tsx` na fullscreen.
 *
 * **Etap 1.4 (edit, ✅ 2026-05-13):** `/users/:id` — pełna strona detail z
 * zakładkami (data / companyAccess / security / audit; dawna zakładka extFields usunięta —
 * pola dodatkowe układa admin w kreatorze jako pojedyncze pola w „Dane", a zapisują się
 * razem z rekordem jednym PATCH-em).
 *
 * **Etap 1.4b + unify (create, ✅ 2026-08-13):** `/users/new` renderuje się przez
 * TEN SAM silnik layoutu co edit (RecursiveFormRenderer + tabRenderers) —
 * `isCreate` steruje edytowalnością/widocznością pól (silnik formatek,
 * `userEngineConfig.tsx`) i podmienia zakładkę company_access na
 * `UserCreateAccessFields` (firma + rola PRZED utworzeniem rekordu). Human → POST
 * `/users/invite` (mail z linkiem). API/Service → POST `/users` (direct, bez
 * maila, header X-Company-Id). Po sukcesie navigate na `/users/:newId`
 * (handleCreateSubmit w tym pliku).
 *
 * Silnik formatek (keystone unifikacja-formatek — mirror `ContractorDetail.tsx`/
 * `CompanyDetail.tsx`): pola core przez `userEngineConfig`, `useKartotekaRenderField`
 * dostarcza `renderField`/`fields` dla `RecursiveFormRenderer` i `UserDataTab`.
 *
 * Tabs (kartoteka-blokify: poza „Dane" wszystkie zakładki to PRZENOŚNE BLOKI —
 * dispatch po TYPIE przez contextRenderers, usuwalne i dodawalne z palety):
 *   - data — UserDataTab (dynamic z layout_config; honoruje nakładkę tab_overrides)
 *   - user_company_access_view — UserCompanyAccessTab (edit) / UserCreateAccessFields (create)
 *   - user_security_view — UserSecurityTab (akcje konta + 2FA badge + strefa zagrożenia)
 *   - user_audit_view — SystemModuleAuditTab (moduleSlug='users'; fallback audit_log_view)
 *   - attachments_view — AttachmentsSection (blok generyczny)
 *
 * Save flow — JEDEN zapis (pola dodatkowe nie mają osobnej zakładki ani osobnego PUT):
 *   - edit: JEDEN PATCH /users/:id (zmienione pola core + extValues + additionalTables w tym
 *     samym body; bez zmian — bez PATCH-a)
 *   - create (człowiek): POST /users/invite z extValues + additionalTables (tabele dodatkowe bez
 *     rodzica, buforowane w stronie; backend zapisuje je w jednej transakcji z nowym kontem);
 *     konto techniczne: POST /users (bez pól i tabel dodatkowych — w create ich nie pokazujemy)
 *
 * Przycisk SQL (klocek układu `sql_button`, contextRenderer wspólny dla silnika i zakładki „Dane”):
 * zadanie w tle na koncie. Edycja — klik najpierw zapisuje (`useSaveBeforeDecision`), podgląd — rusza od
 * razu, nowe konto — utworzenie (zaproszenie / konto techniczne) → zadanie → przejście. Na czas zadania
 * pasek blokady, „Edytuj” i „Usuń” / „Przywróć” (UserSecurityTab) nieaktywne, a zapis, usunięcie i
 * przywrócenie odrzucone 409 `RECORD_LOCKED_BY_RUN` dają czytelny komunikat (mirror ContractorDetail).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isValidUsername } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ArrowLeft, Pencil, Save, X as XIcon } from 'lucide-react';

import { AdditionalTableTab } from '@/components/additional/AdditionalTableTab';
import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { buildDocumentPreviewContextRenderer } from '@/components/attachments/DocumentPreviewContextRenderer';
import { DocumentPreviewDockHost } from '@/components/attachments/DocumentPreviewDockHost';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  type ContextRenderers,
  RecursiveFormRenderer,
} from '@/components/form/RecursiveFormRenderer';
import { SaveFirstNotice } from '@/components/form/SaveFirstNotice';
import { IconButton } from '@/components/IconButton';
import { collectEngineFieldErrors } from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import { RecordRunLockBanner } from '@/components/record-procedures/RecordRunLockBanner';
import {
  collectSqlButtonLabels,
  parseSqlButtonSettings,
} from '@/components/record-procedures/sql-button-settings';
import { SqlButton } from '@/components/record-procedures/SqlButton';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import type { PickedEmployee } from '@/components/users/EmployeeCandidateSelect';
import { UserCompanyAccessTab } from '@/components/users/UserCompanyAccessTab';
import { UserCreateAccessFields } from '@/components/users/UserCreateAccessFields';
import {
  buildUserCoreRecordValues,
  EMPTY_USER_FORM,
  UserDataTab,
  type UserFormData,
} from '@/components/users/UserDataTab';
import {
  userEngineConfig,
  type UserExtra,
  type UserLookupOption,
} from '@/components/users/userEngineConfig';
import { UserSecurityTab } from '@/components/users/UserSecurityTab';
import { useToast } from '@/contexts/ToastContext';
import { useAdditionalTableDeltas } from '@/hooks/useAdditionalTableDeltas';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import { applyCoreDefaults, useNewRecordDefaults } from '@/hooks/useNewRecordDefaults';
import { usePermission } from '@/hooks/usePermission';
import { extValuesDiffer, useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useRecordProcedureRuns } from '@/hooks/useRecordProcedureRuns';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';
import { extractErrorCode, getFieldError } from '@/lib/errors';
import { useSystemModuleFields } from '@/lib/ext-list-columns';
import { mapDictionarySaveError } from '@/lib/records/save-error-message';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useCompanies } from '@/lib/use-permissions';
import {
  USER_DETAIL_KEY,
  type UserDetail as UserDetailType,
  type UserDetailResponse,
} from '@/lib/use-tenant-people';
import {
  buildFieldIssueMessages,
  buildValidationToastMessage,
  resolveFieldLabel,
} from '@/lib/validation-toast';

/** Kody odrzucenia powiązania konta z pracownikiem w PATCH /users/:id (mapowane na `tenant.users.errors.*`). */
const EMPLOYEE_LINK_ERROR_CODES: readonly string[] = [
  'EMPLOYEE_NOT_FOUND',
  'EMPLOYEE_COMPANY_NOT_ACCESSIBLE',
  'EMPLOYEE_ALREADY_LINKED',
  'USER_TYPE_CANNOT_LINK_EMPLOYEE',
];

/**
 * Tryb OSADZONY (okno wyboru opiekuna na dokumencie — „Edytuj" w tabeli użytkowników, mirror
 * `ContractorDetail embedded`): karta działa jako formatka w modalu (`UserFormModal`) zamiast
 * strony pod route'em — id z propa (nie z `useParams`), start od razu w trybie edycji, zakładka
 * NIE synchronizuje się z URL (URL należy do strony-gospodarza, np. faktury), a strzałka
 * wstecz zamyka okno. Tylko edycja — tworzenie użytkownika (zaproszenie) zostaje na stronie.
 */
export interface UserDetailEmbeddedControls {
  userId: string;
  /** Po udanym zapisie — id zapisanego użytkownika. Okna nie zamyka (zamknie je X). */
  onSaved?: (id: string) => void;
  onCancel: () => void;
}

export interface UserDetailProps {
  embedded?: UserDetailEmbeddedControls;
}

export function UserDetail({ embedded }: UserDetailProps = {}) {
  const { t } = useTranslation();
  const routeParams = useParams<{ id?: string }>();
  // Surowy parametr trasy służy WYŁĄCZNIE do rozpoznania trybu tworzenia. Użytkownicy, inaczej niż
  // pozostałe kartoteki, nie mają osobnej trasy tworzenia bez parametru — `/users/new` trafia w
  // `/users/:id`, więc tu siedzi literalne `'new'`.
  const routeId = embedded ? embedded.userId : routeParams.id;
  const isCreate = !routeId || routeId === 'new';
  // Identyfikator ISTNIEJĄCEGO rekordu — jedyne źródło id dla zapytań, zapisu, załączników,
  // podglądu dokumentów, historii zmian, tabel i pól dodatkowych. W trybie tworzenia `undefined`,
  // więc żaden blok nie pyta serwera o rekord `new`.
  const recordId: string | undefined = isCreate ? undefined : routeId;
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();
  // Podswietlenie pol odrzuconych przez backendowy VALIDATION_ERROR (czerwona
  // ramka, bez tekstu — komunikat zostaje w toascie). Patrz onError ponizej.
  const fe = useFieldErrors();

  // Metadane pol (etykiety) do konkretnego komunikatu walidacji w toascie
  // (buildValidationToastMessage). Ten sam queryKey co UserDataTab.tsx —
  // React Query dedupe'uje, wiec brak dodatkowego realnego zadania sieciowego.
  const userFieldsQ = useSystemModuleFields('users');
  const userFieldLabel = (field: string) => resolveFieldLabel(userFieldsQ.data ?? [], field);

  const [searchParams, setSearchParams] = useSearchParams();
  // F12.N E1.T5b: layout w nowym kształcie + role context dla filtrowania zakładek.
  const { layoutConfig, myRoleId, isSuperUser, previewDockEnabled } =
    useSystemModuleActiveLayout('users');
  const initialTab = embedded ? 'data' : (searchParams.get('tab') ?? 'data');
  const [tab, setTabRaw] = useState<string>(initialTab);
  const setTab = (next: string) => {
    setTabRaw(next);
    if (embedded) return;
    const params = new URLSearchParams(searchParams);
    if (next === 'data') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [editing, setEditing] = useState(!!embedded);
  const [form, setForm] = useState<UserFormData>(EMPTY_USER_FORM);
  // Etykieta pracownika wybranego z listy — trzymana tu (nie w polu), bo pole znika i wraca przy
  // przełączeniu podgląd/edycja, a po zapisie rekord odświeża się z opóźnieniem: do tego czasu tryb
  // odczytu bierze etykietę stąd zamiast pokazywać „—" (patrz `employeeIdRenderer`).
  const [pickedEmployee, setPickedEmployee] = useState<PickedEmployee | null>(null);
  // Inny rekord (nawigacja między użytkownikami bez przemontowania strony) — wybór poprzedniego
  // konta nie dotyczy nowego.
  useEffect(() => {
    setPickedEmployee(null);
  }, [recordId]);

  const detailQ = useQuery({
    queryKey: recordId ? USER_DETAIL_KEY(recordId) : ['user', 'none'],
    queryFn: async () => {
      const res = await api.get<UserDetailResponse>(`/users/${recordId}`);
      return res.data;
    },
    enabled: !!recordId,
    staleTime: 0,
  });

  // Przycisk SQL (klocek `sql_button`): zadania wykonywane w tle na tym koncie. Stan (aktywne zadanie +
  // ostatnie per procedura) odpytywany co ~2 s tylko przy aktywnym zadaniu; po jego końcu komunikat w rogu
  // i odświeżenie rekordu (zapytanie mogło go zmienić). Przy tworzeniu brak rekordu. Napisy przycisków z
  // układu służą komunikatom i paskowi blokady (zadanie zna tylko nazwę procedury).
  const sqlButtonLabels = useMemo(() => collectSqlButtonLabels(layoutConfig), [layoutConfig]);
  const runs = useRecordProcedureRuns('user', recordId, {
    labels: sqlButtonLabels,
    onFinished: () => {
      if (recordId) void queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(recordId) });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      void queryClient.invalidateQueries({ queryKey: ['system-module-audit', 'users', recordId] });
    },
  });
  // Prawo uruchomienia = prawo edycji konta (`crm_core.users.edit` w aktywnej firmie). Bramka w panelu to
  // podpowiedź (lista kodów nie zna zawężeń) — o odmowie rozstrzyga serwer. Konto usunięte miękko ma ukryte
  // „Edytuj" (a zapis na nim serwer odrzuca), więc przycisk też jest wyszarzony.
  const userEditPermission = usePermission('crm_core.users.edit');
  const canRunSqlButton = userEditPermission.allowed && detailQ.data?.user.gcrecord == null;
  // Przycisk kliknięty na NOWYM koncie: najpierw utworzenie (`handleCreateSubmit`), potem zadanie z id
  // świeżo utworzonego użytkownika, dopiero potem przejście pod jego adres. Ref niesie zlecenie tylko
  // na czas jednego kliknięcia (czyści je `handleSqlButtonStart`).
  const pendingCreateRunRef = useRef<{ procedureId: string } | null>(null);

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowego użytkownika — pola
  // pokazujemy dopiero po ich wczytaniu (patrz `NewRecordDefaultsStatus` niżej).
  const recordDefaults = useNewRecordDefaults('users', isCreate);

  // Pola dodatkowe w zakładce „Dane": stan lokalny zasilany wartością z odpowiedzi GET rekordu
  // (`user.extValues`, bez osobnego żądania). Pełny kontrakt synchronizacji (w tym ochrona
  // niezapisanych edycji przed odświeżeniem w tle) w nagłówku `useRecordExtValues.ts`. Przy
  // tworzeniu „wartością serwera" są wartości domyślne pól dodatkowych.
  const { extValues, handleExtChange, snapshotEdits, markSaved, discardEdits } = useRecordExtValues(
    {
      recordId,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.user.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // ─── Create-only state (F12.M unify create/edit) ──────────────────────────
  // Firmy + rola wybrane PRZED utworzeniem rekordu (zakładka company_access w
  // trybie create — patrz UserCreateAccessFields). Konsumowane przez
  // handleCreateSubmit. Mirror starego UserCreateForm.
  const [createCompanyIds, setCreateCompanyIds] = useState<string[]>([]);
  const [createRoleTemplateIds, setCreateRoleTemplateIds] = useState<string[]>([]);
  // Domyślna firma konta (users.defaultCompanyId) — wybierana spośród createCompanyIds.
  const [createDefaultCompanyId, setCreateDefaultCompanyId] = useState<string>('');
  const [submittingCreate, setSubmittingCreate] = useState(false);

  // Core invariant (bez tworzenia rekordu bez jawnie wybranej konkretnej firmy):
  // w trybie konkretnej firmy prefill z aktywnego kontekstu; w trybie "Wszystkie
  // firmy" pole zostaje puste i user MUSI wybrać — submit zablokowany w
  // handleCreateSubmit gdy createCompanyIds jest puste.
  const companyScope = useActiveCompanyScope();
  useEffect(() => {
    if (!isCreate) return;
    if (createCompanyIds.length > 0) return;
    if (companyScope.mode !== 'company' || !companyScope.companyId) return;
    const resolvedId = companyScope.companyId;
    setCreateCompanyIds((ids) => (ids.length > 0 ? ids : [resolvedId]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, companyScope.mode, companyScope.companyId]);

  // Domyślna firma zawsze ∈ wybranych firm: gdy bieżąca wypadła z listy (albo jeszcze
  // jej nie ma), spada na pierwszą przypisaną (lub pustą, gdy brak firm). Dzięki temu
  // przy jednej firmie domyślna ustawia się sama, a picker (>=2) startuje z sensownym stanem.
  useEffect(() => {
    if (!isCreate) return;
    setCreateDefaultCompanyId((prev) =>
      prev && createCompanyIds.includes(prev) ? prev : (createCompanyIds[0] ?? ''),
    );
  }, [isCreate, createCompanyIds]);

  // Konto techniczne działa w kontekście JEDNEJ firmy (POST /users bierze ją z nagłówka).
  // Gdy user przełączy typ na api/service po zaznaczeniu kilku firm — zwijamy do pierwszej,
  // żeby stan zgadzał się z pojedynczym pickerem i wysyłką (reszta i tak byłaby ignorowana).
  useEffect(() => {
    if (!isCreate) return;
    if (form.userType !== 'human' && createCompanyIds.length > 1) {
      setCreateCompanyIds((ids) => ids.slice(0, 1));
    }
  }, [isCreate, form.userType, createCompanyIds]);

  // Atomic-save (mirror EmployeeDetail/ContractorDetail): bufor delt tabel dodatkowych i szkiców
  // add-row we wspólnym hooku — kontrakt freshness `getBufferedValue` w nagłówku
  // `useAdditionalTableDeltas.ts`. Delty jadą w TYM SAMYM żądaniu co pola użytkownika
  // (`additionalTables`) — PATCH przy edycji, POST /users/invite przy zakładaniu konta — bez
  // osobnych żądań HTTP.
  const {
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    getAddRowDraft,
    handleAddRowDraftChange,
    resetAddRowDrafts,
  } = useAdditionalTableDeltas();

  // Lista firm tenanta dla renderera `default_company_id` (DefaultCompanySelect) — mirror
  // `ContractorDetail.tsx`'s `companiesQ`/`companyOptions`. Przeniesione z `UserDataTab`
  // (silnik wymaga JEDNEGO kierunku przepływu: strona → runtime.extra → renderer).
  const companiesQ = useCompanies();
  const userCompanyOptions: UserLookupOption[] = (companiesQ.data?.companies ?? []).map((c) => ({
    id: c.id,
    label: c.shortName || c.legalName,
  }));

  // Silnik formatek (keystone unifikacja-formatek — mirror ContractorDetail.tsx/
  // CompanyDetail.tsx) — pola core użytkownika przez `userEngineConfig`. `hasError`/
  // `getError` niosą podświetlenie walidacji (display_name/language); `extra.companies`
  // niesie to, co wcześniej trzymał lokalnie `UserDataTab` (`useCompanies()`).
  const { renderField, fields } = useKartotekaRenderField<UserFormData, UserExtra>(
    userEngineConfig,
    {
      form,
      onChange: setForm,
      extValues,
      onExtChange: handleExtChange,
      // Users, w odróżnieniu od Kontrahentów/Firm, NIE inicjuje `editing` na `isCreate`
      // (`useState(false)` niżej) — tryb create ma pola edytowalne od razu mimo
      // `editing === false`, więc `|| isCreate` musi zostać (1:1 ze starym propem
      // `editable={editing || isCreate}` przekazywanym do `UserDataTab`).
      editable: editing || isCreate,
      isCreate,
      hasError: (key) => !!fe.get(String(key)),
      getError: (key) => fe.get(String(key)),
      clearError: (key) => fe.clear(String(key)),
      extra: {
        companies: userCompanyOptions,
        // Kontekst pola „Pracownik": id konta (zapytanie o kandydatów) + zapisany pracownik.
        userId: recordId ?? null,
        userType: detailQ.data?.user.userType ?? null,
        employee: detailQ.data?.user.employee ?? null,
        pickedEmployee,
        onEmployeePicked: setPickedEmployee,
      },
    },
  );

  // Firma domyślna zakładanego konta — reguła serwera (`create-tenant-user-invite.ts`): wybrana,
  // jeśli jest wśród przypisanych, inaczej pierwsza przypisana. Jedno wyliczenie dla wartości pól
  // rdzenia (ekran) i dla zapisu, żeby nie rozjechały się nawet na jeden render.
  const effectiveCreateDefaultCompanyId =
    createDefaultCompanyId && createCompanyIds.includes(createDefaultCompanyId)
      ? createDefaultCompanyId
      : (createCompanyIds[0] ?? '');

  // Wartości pól rdzenia użytkownika keyed po slugu. Przy zakładaniu konta firma domyślna nie
  // żyje w `form`, tylko w zakładce „Firmy" — bez nadpisania pole `default_company_id` byłoby
  // puste dla warunków widoczności i wiązań `parent_field` słowników w tabelach dodatkowych.
  const coreRecordValues = useMemo(
    () =>
      buildUserCoreRecordValues(
        isCreate ? { ...form, defaultCompanyId: effectiveCreateDefaultCompanyId || null } : form,
      ),
    [isCreate, form, effectiveCreateDefaultCompanyId],
  );

  // Wartości formularza keyed po slugu (core + ext) — do oceny warunków widoczności
  // pól/bloków z kreatora. Przekazywane do UserDataTab i RecursiveFormRenderer.
  const layoutFormData = useMemo<Record<string, unknown>>(
    () => ({ ...coreRecordValues, ...extValues }),
    [coreRecordValues, extValues],
  );

  // Wartości pól użytkownika (core + dodatkowe) JAK NA EKRANIE, także niezapisane zmiany —
  // źródło wiązań `parent_field` słowników w wierszach tabel dodatkowych (zakładki dostają je
  // jako `parentValues`). Skalary/null; przy kolizji sluggu wygrywa nagłówek (core) — ta sama
  // kolejność scalania co w pracownikach i kontrahentach. Stabilność referencji nie jest wymagana.
  const additionalOwnerValues = useMemo(
    () => buildAdditionalViewRecordValues(extValues, coreRecordValues),
    [coreRecordValues, extValues],
  );

  // Nowy użytkownik: wartości domyślne pól nadpisują wartości startowe z kodu. `recordDefaults.core`
  // zmienia referencję raz (wczytanie) — pola są wtedy jeszcze ukryte, więc nic wpisanego nie ginie.
  useEffect(() => {
    if (!isCreate) return;
    setForm(applyCoreDefaults(EMPTY_USER_FORM, recordDefaults.core));
  }, [isCreate, recordDefaults.core]);

  useEffect(() => {
    if (!detailQ.data?.user) return;
    const u = detailQ.data.user;
    setForm({
      displayName: u.displayName,
      language: u.language,
      defaultCompanyId: u.defaultCompanyId,
      email: u.email,
      username: '', // UserDetail response nie ma username — wczytanie z list ma sens dopiero w Etapie 1.4b
      userType: u.userType,
      isSuperUser: u.isSuperUser,
      status: u.status,
      lastLoginAt: u.lastLoginAt,
      invitedAt: u.invitedAt,
      groups: u.groups ?? [],
      categoryId: u.categoryId ?? null,
      employeeId: u.employeeId ?? null,
    });
  }, [detailQ.data]);

  // Klik przycisku SQL, gdy formularz jest w edycji: najpierw ten sam zapis co „Zapisz”, zadanie dopiero po
  // jego sukcesie (useSaveBeforeDecision; „zapis już trwa" sprawdza sam hak po kluczu `saveMutationKey`
  // mutacji zapisu tej kopii formularza). `succeeded` / `aborted` są wołane z `saveMutation.onSuccess/onError`.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId,
    save: handleSave,
  });

  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: UserFormData) => {
      // Zapis edycji dotyczy wyłącznie istniejącego rekordu (przycisk „Zapisz" jest tylko poza
      // trybem tworzenia) — brak id to błąd programisty, nie PATCH na `/users/undefined`.
      if (!recordId) throw new Error('[UserDetail] zapis edycji bez identyfikatora rekordu');
      const payload: Record<string, unknown> = {};
      if (data.displayName !== detailQ.data?.user.displayName) {
        payload.displayName = data.displayName;
      }
      if (data.language !== detailQ.data?.user.language) {
        payload.language = data.language;
      }
      if (data.defaultCompanyId !== detailQ.data?.user.defaultCompanyId) {
        payload.defaultCompanyId = data.defaultCompanyId;
      }
      // Grupy — porównanie po TREŚCI (nowa tablica co render), inaczej payload leciałby
      // przy każdym zapisie i nadpisywał grupy zmienione w międzyczasie z innej sesji.
      const storedGroups = detailQ.data?.user.groups ?? [];
      const sameGroups =
        storedGroups.length === data.groups.length &&
        storedGroups.every((code, i) => code === data.groups[i]);
      if (!sameGroups) {
        payload.groups = data.groups;
      }
      if (data.categoryId !== (detailQ.data?.user.categoryId ?? null)) {
        payload.categoryId = data.categoryId;
      }
      // Pracownik konta — `null` = odpięcie (serwer: PATCH `employeeId: null`).
      if (data.employeeId !== (detailQ.data?.user.employeeId ?? null)) {
        payload.employeeId = data.employeeId;
      }
      // Atomic-save (mirror ContractorDetail/EmployeeDetail): jeden zapis — zmienione pola
      // dodatkowe i delty tabel dodatkowych jadą w TYM SAMYM PATCH co pola core (backend zapisuje
      // wszystko w jednej transakcji, saveAggregate); zero osobnego PUT ext-fields. Pola dodatkowe
      // wysyłamy tylko gdy różnią się od serwera — „nic się nie zmieniło" nie wysyła pustego PATCH.
      // Migawka edycji tuż przed wysłaniem: edycja zrobiona w trakcie żądania nie zostanie zdjęta
      // przez `markSaved()` w onSuccess (patrz nagłówek `useRecordExtValues.ts`).
      snapshotEdits();
      if (extValuesDiffer(extValues, detailQ.data?.user.extValues)) {
        payload.extValues = extValues;
      }
      const additionalTablesBody = flattenAdditionalDeltas();
      if (additionalTablesBody) {
        payload.additionalTables = additionalTablesBody;
        // Wartości pól użytkownika z ekranu dla wiązań `parent_field` w wierszach tabel
        // dodatkowych — OBOK delt, tylko slugi użyte przez wiązania, tylko skalary.
        const additionalParentValues = await buildAdditionalParentValues(
          buildAdditionalViewRecordValues(extValues, buildUserCoreRecordValues(data)),
          'default',
        );
        if (additionalParentValues) {
          payload.additionalParentValues = additionalParentValues;
        }
      }
      if (Object.keys(payload).length > 0) {
        await api.patch(`/users/${recordId}`, payload);
      }
    },
    onSuccess: () => {
      // Udany zapis gasi podświetlenia z poprzedniej nieudanej próby (ramka + tekst).
      fe.clearAll();
      // Pola dodatkowe zapisane razem z użytkownikiem — od tej chwili wartość serwera (z odświeżonego
      // rekordu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      toast.show({ variant: 'success', title: t('tenant.users.toast.updated') });
      if (recordId) {
        queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(recordId) });
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // Etykiety użytkownika w polach wyboru (np. „Opiekun" na dokumencie) i w tabeli wyboru
      // mają własne klucze — bez tego po zmianie nazwy pole pokazuje starą przez staleTime.
      queryClient.invalidateQueries({ queryKey: ['users-for-picker'] });
      if (recordId) {
        queryClient.invalidateQueries({ queryKey: ['users-for-picker-detail', recordId] });
      }
      queryClient.invalidateQueries({ queryKey: ['record-table-picker-list', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['record-table-picker-prefetch', 'users'] });
      // Atomic-save: backend już zapisał tabele dodatkowe atomowo — odśwież rekordy (prefix match
      // łapie wszystkie ['additional-records', tableId, parentId]) i wyczyść lokalny bufor delt.
      queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      resetAdditionalDeltas();
      // Finding [G] (pre-commit-verify): szkic niezatwierdzonego add-row w tabelach
      // dodatkowych czyścimy po udanym zapisie strony, spójnie z pozostałymi kartotekami.
      resetAddRowDrafts();
      // `setEditing(false)` i `succeeded()` razem, bez `await` między nimi (wymóg haka). Zadanie przycisku
      // SQL działa na danych już zapisanych w bazie, więc nie czeka na odświeżenie rekordu w formularzu.
      setEditing(false);
      if (recordId) embedded?.onSaved?.(recordId);
      // Zapis wywołany kliknięciem przycisku SQL → teraz startuje zadanie (no-op, gdy zapis poszedł
      // zwykłym „Zapisz").
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      // Zapis wywołany kliknięciem przycisku SQL: każdy błąd kończy sprawę — zadanie się nie uruchomi
      // (bramka zapomina czekające zlecenie).
      saveBeforeDecision.aborted();
      // Podświetlenie + tekst pod polem odrzuconym przez serwer. Kolejność komunikatu:
      // (1) polski z issue Zoda, (2) własny komunikat serwera z `details`, (3) generyk.
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      const code = extractErrorCode(err);
      if (code === 'VALIDATION_ERROR') {
        const specific = buildValidationToastMessage(err, userFieldLabel, t);
        toast.show({
          variant: 'error',
          title: specific ?? t('tenant.users.toast.validationError'),
        });
        return;
      }
      // Odrzucona część słownikowa zapisu (pozycja spoza słownika / brak lub zła wartość
      // parametru słownika) — czytelna przyczyna zamiast ogólnego „błąd zapisu".
      const dictionaryMsg = mapDictionarySaveError(err, t);
      if (dictionaryMsg !== null) {
        toast.show({ variant: 'error', title: dictionaryMsg });
        return;
      }
      // Zapis odrzucony, bo na koncie trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „konflikt”; odświeżony stan zadań pokaże pasek blokady.
      if (runs.handleLockedError(err)) return;
      // Odrzucone powiązanie z pracownikiem — konkretna przyczyna (brak pracownika / brak dostępu
      // konta do jego firmy / pracownik zajęty przez inne konto) zamiast ogólnego konfliktu.
      if (code && EMPLOYEE_LINK_ERROR_CODES.includes(code)) {
        toast.show({ variant: 'error', title: t(`tenant.users.errors.${code}`) });
        return;
      }
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.show({ variant: 'error', title: t('tenant.users.toast.conflict') });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.users.toast.updateError') });
    },
  });

  function handleCancel() {
    // Anulowanie gasi podświetlenia z nieudanej próby zapisu (ramka + tekst pod polem).
    fe.clearAll();
    // „Anuluj" wyrzuca też zbuforowane zmiany w tabelach dodatkowych — inaczej wyciekłyby do
    // kolejnego PATCH po ponownym wejściu w edycję.
    resetAdditionalDeltas();
    resetAddRowDrafts();
    // Wybór pracownika z porzuconej edycji nie ma już czego opisywać.
    setPickedEmployee(null);
    // W oknie (tryb osadzony) „Anuluj" zamyka okno bez zapisu — jak przy kontrahencie.
    if (embedded) {
      embedded.onCancel();
      return;
    }
    setEditing(false);
    discardEdits();
    if (detailQ.data?.user) {
      const u = detailQ.data.user;
      setForm((prev) => ({
        ...prev,
        displayName: u.displayName,
        language: u.language,
        defaultCompanyId: u.defaultCompanyId,
        employeeId: u.employeeId ?? null,
      }));
    }
  }

  // Wspólna bramka formatu wg TYPU pola (oś silnika): e-mail/URL/liczba/data dla pól
  // core (config, po stateKey) i dodatkowych (katalog, po slugu). Zwraca true gdy
  // można wysłać, false gdy zablokowano (podświetla pola + toast). Wspólna dla ścieżki
  // edycji (`handleSave`) i tworzenia (`handleCreateSubmit`).
  function runFormatGate(): boolean {
    const fieldErrors = collectEngineFieldErrors(userEngineConfig, form, fields, extValues, t);
    if (Object.keys(fieldErrors).length > 0) {
      fe.clearAll();
      for (const [key, msg] of Object.entries(fieldErrors)) {
        fe.set(key, msg);
      }
      toast.show({ variant: 'error', title: t('common.validationIssue.formToast') });
      return false;
    }
    return true;
  }

  // Zwraca, czy zapis wystartował (false = zatrzymała go walidacja w panelu) — bramka przycisku SQL i
  // `useSaveBeforeDecision` czekają tylko na zapis, który naprawdę ruszył.
  function handleSave(): boolean {
    if (!runFormatGate()) return false;
    saveMutation.mutate(form);
    return true;
  }

  /**
   * Start zadania przycisku SQL. W edycji i podglądzie wołany PO bramce zapisu (edycja: najpierw zwykły
   * zapis, błąd zapisu = zadanie nie startuje; podgląd: od razu). Na nowym koncie zapamiętuje zadanie i
   * uruchamia zwykłe tworzenie (`handleCreateSubmit`), które zleca je z id świeżo utworzonego użytkownika
   * PRZED przejściem pod jego adres. Obietnica kończy się razem z tworzeniem (także gdy walidacja formularza
   * je zatrzymała) — przycisk wraca wtedy do gotowości, a zadanie nie zostaje zapamiętane.
   */
  function handleSqlButtonStart(procedureId: string): Promise<unknown> {
    if (!isCreate) return runs.startRun(procedureId);
    // Tworzenie już trwa (kliknięte „Zaproś" / „Utwórz konto") — drugie wywołanie utworzyłoby konto drugi raz.
    if (submittingCreate) return Promise.resolve();
    pendingCreateRunRef.current = { procedureId };
    return handleCreateSubmit().finally(() => {
      pendingCreateRunRef.current = null;
    });
  }

  /**
   * handleCreateSubmit — F12.M unify create/edit (dawny UserCreateForm.handleSubmit).
   * 2 ścieżki tworzenia (decyzja z F10 / ADR-0022):
   *  - **human** → POST /users/invite z {email, username, companyIds, roleTemplateIds?,
   *    displayName?, language, extValues?, additionalTables?}. Backend zapisuje konto, pola i
   *    wiersze tabel dodatkowych w jednej transakcji i wysyła mail z linkiem do hasła.
   *  - **api/service** → POST /users z {email, displayName?, userType, language} +
   *    header X-Company-Id (endpoint NIE przyjmuje username/companyIds/extValues).
   * Po sukcesie → navigate na /users/:newId.
   */
  async function handleCreateSubmit() {
    if (submittingCreate) return;

    const email = form.email.trim();
    if (!email) {
      toast.show({ variant: 'error', title: t('tenant.users.detail.create.toast.emailRequired') });
      return;
    }

    const isHuman = form.userType === 'human';
    const username = form.username.trim();

    if (isHuman && !username) {
      toast.show({
        variant: 'error',
        title: t('tenant.users.detail.create.toast.usernameRequired'),
      });
      return;
    }

    if (createCompanyIds.length === 0) {
      toast.show({ variant: 'error', title: t('common.companyRequired') });
      return;
    }

    // Rola NIE jest wymagana — użytkownik bez roli jest dozwolony (menu i uprawnienia można mu
    // nadać przez zespół albo indywidualnie). Backend przyjmuje brak ról (roleTemplateIds opcjonalne).

    // Walidacja formatu loginu PRZED wyslaniem (male litery, cyfry, kropka, _, -).
    if (isHuman && !isValidUsername(username)) {
      toast.show({ variant: 'error', title: t('tenant.users.createModal.errors.USERNAME_FORMAT') });
      return;
    }

    // Format wg TYPU pola (e-mail/URL/liczba/data dla pól core i dodatkowych) — ta sama
    // oś silnika co przy edycji; puste pola przepuszcza sam walidator.
    if (!runFormatGate()) return;

    setSubmittingCreate(true);
    try {
      let newUserId: string;
      if (isHuman) {
        const body: Record<string, unknown> = {
          email,
          username,
          companyIds: createCompanyIds,
          language: form.language,
        };
        if (form.displayName?.trim()) body.displayName = form.displayName.trim();
        if (createRoleTemplateIds.length > 0) body.roleTemplateIds = createRoleTemplateIds;
        // Domyślna firma — to samo wyliczenie co wartości pól na ekranie (backend też waliduje).
        if (effectiveCreateDefaultCompanyId) {
          body.defaultCompanyId = effectiveCreateDefaultCompanyId;
        }
        if (Object.keys(extValues).length > 0) body.extValues = extValues;
        // Wiersze tabel dodatkowych z bufora strony — ta sama funkcja spłaszczania co przy PATCH.
        const additionalTablesBody = flattenAdditionalDeltas();
        if (additionalTablesBody) {
          body.additionalTables = additionalTablesBody;
          // Wartości pól użytkownika z ekranu dla wiązań `parent_field` (jak przy PATCH).
          const additionalParentValues = await buildAdditionalParentValues(
            buildAdditionalViewRecordValues(extValues, coreRecordValues),
            'default',
          );
          if (additionalParentValues) body.additionalParentValues = additionalParentValues;
        }
        const res = await api.post<{ user: { id: string } }>('/users/invite', body);
        newUserId = res.data.user.id;
        // Backend zapisał wiersze atomowo z kontem — czyścimy bufor (i szkice wiersza dodawania)
        // jak po udanym PATCH. Strona po nawigacji na `/users/:newId` może zostać tą samą
        // instancją komponentu, a stary bufor nałożyłby się wtedy na zapisane wiersze i poleciał
        // w kolejnym PATCH.
        resetAdditionalDeltas();
        resetAddRowDrafts();
        toast.show({ variant: 'success', title: t('tenant.users.detail.create.toast.invited') });
      } else {
        // POST /users (api/service) NIE przyjmuje companyId w body — backend wymaga
        // go WYŁĄCZNIE przez header X-Company-Id (400 COMPANY_CONTEXT_REQUIRED gdy
        // brak). Interceptor osi w trybie "Wszystkie firmy" świadomie NIE dopina
        // headera z localStorage, więc wysyłamy go jawnie per-request.
        const res = await api.post<{ user: { id: string } }>(
          '/users',
          {
            email,
            displayName: form.displayName?.trim() || undefined,
            userType: form.userType,
            language: form.language,
            categoryId: form.categoryId ?? undefined,
          },
          { headers: { 'X-Company-Id': createCompanyIds[0] } },
        );
        newUserId = res.data.user.id;
        toast.show({ variant: 'success', title: t('tenant.users.detail.create.toast.created') });
      }
      // Przycisk SQL kliknięty na nowym koncie: zadanie rusza TERAZ — z id nowego użytkownika, PRZED
      // przejściem pod jego adres. Zlecenie nie rzuca (porażkę zgłasza komunikatem w rogu), a konto już
      // istnieje, więc przejście i tak następuje.
      const pendingRun = pendingCreateRunRef.current;
      pendingCreateRunRef.current = null;
      if (pendingRun !== null) await runs.startRun(pendingRun.procedureId, newUserId);
      // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab) — silnik kart
      // zamienia ją w miejscu zamiast otwierać drugą. Reset trybu podglądu NIE jest tu
      // potrzebny: Users nie inicjuje `editing` na `isCreate` (pola create są edytowalne mimo
      // `editing === false`, patrz `editable: editing || isCreate`).
      navigate(withMenuItem(`/users/${newUserId}`), { state: { replaceTab: true } });
    } catch (err) {
      // Podświetlenie + tekst pod polem odrzuconym przez serwer (musi zobaczyć błąd PRZED
      // każdą gałęzią z wczesnym `return`, patrz JSDoc `useFieldErrors.ts`).
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      // Serwerowy VALIDATION_ERROR (Zod) na polu username -> komunikat dedykowany.
      if (getFieldError(err, 'username')) {
        toast.show({
          variant: 'error',
          title: t('tenant.users.createModal.errors.USERNAME_FORMAT'),
        });
        setSubmittingCreate(false);
        return;
      }
      // Odrzucona część słownikowa zapisu (pozycja spoza słownika / brak lub zła wartość
      // parametru słownika) — czytelna przyczyna; wygrywa z ogólnym mapowaniem kodu niżej.
      const dictionaryMsg = mapDictionarySaveError(err, t);
      if (dictionaryMsg !== null) {
        toast.show({ variant: 'error', title: dictionaryMsg });
        setSubmittingCreate(false);
        return;
      }
      if (isAxiosError(err)) {
        const code = err.response?.data?.error as string | undefined;
        if (code) {
          const msg = t(`tenant.users.createModal.errors.${code}`, { defaultValue: '' });
          if (msg) {
            toast.show({ variant: 'error', title: msg });
            setSubmittingCreate(false);
            return;
          }
        }
      }
      toast.show({ variant: 'error', title: t('tenant.users.detail.create.toast.error') });
      setSubmittingCreate(false);
    }
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="user-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div
        className="m-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        data-testid="user-detail-error"
      >
        {t('tenant.users.detail.loadError')}
      </div>
    );
  }

  // Nowy użytkownik: pola dopiero po wczytaniu wartości domyślnych (albo komunikat z „Spróbuj
  // ponownie”). Po wszystkich hookach.
  if (isCreate && recordDefaults.status !== 'ready') {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="user-detail" />;
  }

  const user: UserDetailType | undefined = detailQ.data?.user;
  const isDeleted = user?.gcrecord != null;
  const displayName = user?.displayName ?? user?.email ?? '';
  // Konto zablokowane na czas zadania przycisku SQL (decyzja „A”): aktywne zadanie albo właśnie wysyłane
  // zlecenie — „Edytuj” oraz „Usuń” / „Przywróć” (zakładka „Bezpieczeństwo”) nieaktywne; serwer odrzuca
  // te operacje tak czy inaczej.
  const recordLocked = runs.active !== null || runs.starting;

  // Klocek „Przycisk SQL”: zadanie w tle na tym koncie. W edycji klik najpierw zapisuje rekord (bramka
  // `useSaveBeforeDecision`; błąd zapisu = zadanie nie rusza), w podglądzie rusza od razu, na nowym
  // koncie — utworzenie (zaproszenie / konto techniczne), zadanie, przejście. Prawo: ten sam sygnał co
  // zapis (serwer i tak sprawdza). „Zapisz" i tworzenie w toku też blokują przycisk — drugi klik nie
  // może wysłać drugiego zapisu. Identyfikatory testowe per klocek (`sql-button-<id klocka>`): strona
  // może mieć kilka przycisków. Klocek bez procedury / napisu: nic.
  const renderSqlButton: ContextRenderers[string] = ({ element }) => {
    const cfg = parseSqlButtonSettings(element.settings);
    if (cfg === null) return null;
    return (
      <SqlButton
        label={cfg.label}
        procedureId={cfg.procedureId}
        active={runs.active}
        lastRun={runs.lastByProcedure.find((run) => run.procedureId === cfg.procedureId)}
        canEdit={canRunSqlButton}
        // Po utworzeniu konta strona zostaje tą samą instancją, a `submittingCreate` nie jest zerowane —
        // dlatego liczy się tylko w trybie tworzenia.
        starting={runs.starting || saveMutation.isPending || (isCreate && submittingCreate)}
        beforeStart={isCreate ? undefined : saveBeforeDecision.beforeDecision}
        onStart={(procedureId) => handleSqlButtonStart(procedureId)}
        testIdPrefix={`sql-button-${element.id}`}
      />
    );
  };

  // Zakładka „Dane” (UserDataTab) dostaje wyłącznie renderer przycisku SQL — pozostałe klocki w „Dane”
  // rysują się tak jak przed przyciskiem (zmiana ich renderowania jest poza zakresem przycisku).
  const userDataTabRenderers: ContextRenderers = { sql_button: renderSqlButton };

  return (
    <DocumentPreviewDockHost
      enabled={previewDockEnabled && !!recordId}
      moduleId="users"
      ownerType="user"
      ownerId={recordId}
    >
      <div className="space-y-4 p-6" data-testid="user-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* W oknie (tryb osadzony) zamyka je własny X okna — strzałka wstecz zbędna
                (mirror `ContractorDetail embedded`). */}
            {!embedded && (
              <IconButton
                onClick={() => navigate(withMenuItem('/users'))}
                data-testid="user-detail-back"
                aria-label={t('common.back')}
              >
                <ArrowLeft className="h-5 w-5" />
              </IconButton>
            )}
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">
                {isCreate ? t('tenant.users.detail.create.title') : displayName}
              </h1>
              {!isCreate && (
                <div className="mt-1 flex items-center gap-2 text-sm text-text-tertiary">
                  {user?.email}
                  <Badge color={user?.userType === 'human' ? 'blue' : 'gray'}>
                    {t(`tenant.users.userType.${user?.userType ?? 'human'}`)}
                  </Badge>
                  {user?.isSuperUser && (
                    <Badge color="accent">{t('tenant.users.badge.superUser')}</Badge>
                  )}
                  <Badge
                    color={
                      user?.status === 'active'
                        ? 'green'
                        : user?.status === 'suspended'
                          ? 'red'
                          : 'amber'
                    }
                  >
                    {t(`tenant.users.status.${user?.status ?? 'active'}`)}
                  </Badge>
                  <Badge color={user?.totpEnabled ? 'green' : 'gray'}>
                    {t(user?.totpEnabled ? 'tenant.users.badge.twoFA' : 'tenant.users.badge.no2FA')}
                  </Badge>
                  {isDeleted && <Badge color="red">{t('tenant.users.badge.deleted')}</Badge>}
                </div>
              )}
            </div>
          </div>

          {/* Akcje */}
          <div className="flex items-center gap-2">
            {isCreate && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(withMenuItem('/users'))}
                  disabled={submittingCreate}
                  data-testid="user-create-cancel-btn"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Save size={14} aria-hidden="true" />}
                  onClick={handleCreateSubmit}
                  disabled={submittingCreate}
                  data-testid="user-create-submit-btn"
                >
                  {form.userType === 'human'
                    ? t('tenant.users.detail.create.submitInvite')
                    : t('tenant.users.detail.create.submitCreate')}
                </Button>
              </>
            )}
            {!isCreate && !editing && !isDeleted && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Pencil size={14} aria-hidden="true" />}
                onClick={() => setEditing(true)}
                disabled={recordLocked}
                data-testid="user-detail-edit-btn"
              >
                {t('common.edit')}
              </Button>
            )}
            {!isCreate && editing && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Save size={14} aria-hidden="true" />}
                  onClick={() => handleSave()}
                  disabled={saveMutation.isPending}
                  data-testid="user-detail-save-btn"
                >
                  {t('common.save')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<XIcon size={14} aria-hidden="true" />}
                  onClick={handleCancel}
                  disabled={saveMutation.isPending}
                  data-testid="user-detail-cancel-btn"
                >
                  {t('common.cancel')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Zadanie przycisku SQL na koncie (w kolejce lub w toku): konta nie da się zapisać, usunąć ani
          przywrócić, „Edytuj”, „Usuń” i „Przywróć” są nieaktywne; pasek znika po zakończeniu zadania. */}
        {runs.active ? (
          <RecordRunLockBanner
            run={runs.active}
            label={runs.labelFor(runs.active)}
            refreshFailed={runs.isError}
          />
        ) : null}

        {/* F12.N E1.T5b: brak layoutu (fallback) → sam UserDataTab bez top-bara.
          Z layoutem → RecursiveFormRenderer rendruje zakładki + tabRenderers
          delegują content per seed-slug. */}
        {!layoutConfig ? (
          <UserDataTab
            contextRenderers={userDataTabRenderers}
            renderField={renderField}
            fields={fields}
            formData={layoutFormData}
            isCreate={isCreate}
            userType={form.userType}
          />
        ) : (
          <RecursiveFormRenderer
            layoutConfig={layoutConfig}
            activeTabId={tab}
            onTabChange={setTab}
            myRoleId={myRoleId}
            isSuperUser={isSuperUser}
            renderField={renderField}
            formData={layoutFormData}
            tabRenderers={{
              // Tylko zakładka „Dane" (typ fields) renderuje się przez tabRenderers.
              // Reszta zakładek (Firmy / Bezpieczeństwo / Historia zmian / Załączniki) to
              // przenośne BLOKI — dispatch po TYPIE przez contextRenderers niżej,
              // więc są usuwalne i dodawalne z palety (kartoteka-blokify, „nic nie ginie").
              // „Pola dodatkowe" nie mają osobnej zakładki: pola układa w „Dane" silnik formatek.
              data: () => (
                <UserDataTab
                  contextRenderers={userDataTabRenderers}
                  renderField={renderField}
                  fields={fields}
                  formData={layoutFormData}
                  isCreate={isCreate}
                  userType={form.userType}
                />
              ),
            }}
            contextRenderers={{
              // User nie jest encją per-firma (dostęp do wielu firm przez
              // user_company_access, patrz UserCompanyAccessTab) — brak companyId,
              // analogicznie do reszty tej strony w trybie edit (żaden inny renderer
              // tutaj nie przekazuje companyId). Załączniki wymagają zapisanego rekordu — przy
              // tworzeniu nic, jak na pozostałych kartach (kartoteki i dokumenty).
              attachments_view: () =>
                recordId ? <AttachmentsSection ownerType="user" ownerId={recordId} /> : null,
              // Konfigurowalne zakładki — „Dostęp do firm" jako przenośny blok-panel.
              // W trybie create: wybór firm + roli PRZED utworzeniem rekordu
              // (UserCreateAccessFields); w edit: macierz dostępu (UserCompanyAccessTab).
              user_company_access_view: () =>
                isCreate ? (
                  <UserCreateAccessFields
                    userType={form.userType}
                    companyIds={createCompanyIds}
                    onCompanyIdsChange={setCreateCompanyIds}
                    roleTemplateIds={createRoleTemplateIds}
                    onRoleTemplateIdsChange={setCreateRoleTemplateIds}
                    defaultCompanyId={createDefaultCompanyId}
                    onDefaultCompanyIdChange={setCreateDefaultCompanyId}
                  />
                ) : user ? (
                  <UserCompanyAccessTab userId={user.id} isSuperUser={user.isSuperUser} />
                ) : (
                  <SaveFirstNotice message={t('tenant.users.detail.tabDisabled.save')} />
                ),
              // Konfigurowalne zakładki — „Bezpieczeństwo" jako przenośny blok-panel
              // (akcje konta, 2FA, sesje, strefa zagrożenia). Wymaga zapisanego rekordu. „Usuń" i „Przywróć"
              // to trasy blokowane na czas zadania przycisku SQL — panel dostaje stan blokady i obsługę 409.
              user_security_view: () =>
                user ? (
                  <UserSecurityTab
                    user={user}
                    recordLocked={recordLocked}
                    onLockedError={runs.handleLockedError}
                  />
                ) : (
                  <SaveFirstNotice message={t('tenant.users.detail.tabDisabled.save')} />
                ),
              // Konfigurowalne zakładki — „Historia zmian" jako przenośny blok per kartoteka
              // (`user_audit_view`). Stary wspólny `audit_log_view` zostaje jako fallback
              // dla layoutów sprzed blokify.
              user_audit_view: () =>
                recordId ? (
                  <SystemModuleAuditTab
                    moduleSlug="users"
                    recordId={recordId}
                    testIdPrefix="user-audit"
                  />
                ) : (
                  <SaveFirstNotice message={t('tenant.users.detail.tabDisabled.save')} />
                ),
              audit_log_view: () =>
                recordId ? (
                  <SystemModuleAuditTab
                    moduleSlug="users"
                    recordId={recordId}
                    testIdPrefix="user-audit"
                  />
                ) : (
                  <SaveFirstNotice message={t('tenant.users.detail.tabDisabled.save')} />
                ),
              // Fallback kuloodporny dla layoutów sprzed blokify (non-pristine, bez re-seedu):
              // zakładka „Firmy" była generycznym `related_records_table` (collection=user_company_access).
              // Po re-seedzie emituje `user_company_access_view`. Inne kolekcje — nie nasze, pomiń.
              related_records_table: ({ element }) => {
                const collection =
                  typeof element?.settings?.collection === 'string'
                    ? element.settings.collection
                    : '';
                if (collection !== 'user_company_access') return null;
                return isCreate ? (
                  <UserCreateAccessFields
                    userType={form.userType}
                    companyIds={createCompanyIds}
                    onCompanyIdsChange={setCreateCompanyIds}
                    roleTemplateIds={createRoleTemplateIds}
                    onRoleTemplateIdsChange={setCreateRoleTemplateIds}
                    defaultCompanyId={createDefaultCompanyId}
                    onDefaultCompanyIdChange={setCreateDefaultCompanyId}
                  />
                ) : user ? (
                  <UserCompanyAccessTab userId={user.id} isSuperUser={user.isSuperUser} />
                ) : (
                  <SaveFirstNotice message={t('tenant.users.detail.tabDisabled.save')} />
                );
              },
              // Fallback kuloodporny dla layoutów sprzed blokify: zakładka „Bezpieczeństwo"
              // była generycznym `panel_view`. Po re-seedzie emituje `user_security_view`.
              panel_view: () =>
                user ? (
                  <UserSecurityTab
                    user={user}
                    recordLocked={recordLocked}
                    onLockedError={runs.handleLockedError}
                  />
                ) : (
                  <SaveFirstNotice message={t('tenant.users.detail.tabDisabled.save')} />
                ),
              // Przy tworzeniu bez właściciela → pusty stan podglądu, bez zapytania o załączniki.
              ...buildDocumentPreviewContextRenderer({
                ownerType: 'user',
                ownerId: recordId,
                extValues,
              }),
              // Atomic-save (mirror EmployeeDetail): tabele dodatkowe buforowane — delty jadą w PATCH
              // (edycja) albo w POST /users/invite (zakładanie konta) razem z resztą body
              // (`additionalTables`). `getBufferedValue` czyta AKTUALNĄ wartość z bufora hooka
              // przy każdym renderze, więc remount panelu (zmiana zakładki) nie zeruje bufora.
              // Tworzenie: rekordu jeszcze nie ma — tabela BEZ rodzica (`parentId={null}`, nie
              // `'new'` z adresu), bez odczytu z serwera, od razu edytowalna; backend zapisuje
              // wiersze w jednej transakcji z nowym kontem. Konto techniczne (api/service) w create
              // tabel nie dostaje — POST /users ich nie przyjmuje, więc wpisane wiersze by przepadły
              // (ta sama bramka co dla pól dodatkowych w `UserDataTab`).
              // Tabela dodatkowa zajmuje całą szerokość sekcji (`col-span-full`) — tak samo jak
              // w podglądzie kreatora formatki; bez tego trafiłaby w jedną komórkę siatki.
              additional_view: ({ element }) => {
                const tableSlug =
                  typeof element?.settings?.tableSlug === 'string'
                    ? element.settings.tableSlug
                    : '';
                if (isCreate && form.userType !== 'human') return null;
                return tableSlug ? (
                  <div className="col-span-full">
                    <AdditionalTableTab
                      tableSlug={tableSlug}
                      parentId={recordId ?? null}
                      editable={editing || isCreate}
                      buffered
                      onDeltasChange={handleAdditionalDeltasChange}
                      getBufferedValue={getBufferedValue}
                      getAddRowDraft={getAddRowDraft}
                      onAddRowDraftChange={handleAddRowDraftChange}
                      parentValues={additionalOwnerValues}
                      parentKey="default"
                      columnSettings={element.settings}
                    />
                  </div>
                ) : null;
              },
              satellite_view: ({ element }) => {
                const tableSlug =
                  typeof element?.settings?.tableSlug === 'string'
                    ? element.settings.tableSlug
                    : '';
                if (isCreate && form.userType !== 'human') return null;
                return tableSlug ? (
                  <div className="col-span-full">
                    <AdditionalTableTab
                      tableSlug={tableSlug}
                      parentId={recordId ?? null}
                      editable={editing || isCreate}
                      buffered
                      onDeltasChange={handleAdditionalDeltasChange}
                      getBufferedValue={getBufferedValue}
                      getAddRowDraft={getAddRowDraft}
                      onAddRowDraftChange={handleAddRowDraftChange}
                      parentValues={additionalOwnerValues}
                      parentKey="default"
                      columnSettings={element.settings}
                    />
                  </div>
                ) : null;
              },
              sql_button: renderSqlButton,
            }}
          />
        )}
      </div>
    </DocumentPreviewDockHost>
  );
}
